import {
  AddShoppingCartRounded,
  FavoriteBorderOutlined,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import React from "react";
import { useNavigate } from "react-router-dom";
import CustomTooltip from "../../../../utils/CustomTooltip";
import { RevealFade } from "../../../../utils/Reveal";
import { IProductResponseView } from "../../models/IProductResponseView";

interface ProductCardProps {
  product: IProductResponseView;
  clickAddToCart: (product: IProductResponseView) => void;
  categoryName: string | undefined;
}

const ProductCard2: React.FC<ProductCardProps> = ({
  product,
  clickAddToCart,
  categoryName,
}) => {
  const navigate = useNavigate();
  const productId = product._id;

  return (
    <Box>
      <Card
        elevation={0}
        sx={{
          maxWidth: { xs: "100%", sm: "100%" },
          width: { xs: "100%", sm: "100%" },
          aspectRatio: { xs: "1/1.99", sm: "1 / 1.85",md: "1 / 1.85",lg: "1 / 1.85" }, 
          maxHeight: { xs: 350, sm: 370,md:400,lg:420, xl: 440 },
          height: "auto",
          borderRadius: "10px",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <RevealFade>
          <CardMedia
            component="img" // Ensure CardMedia renders as an img tag
            height="auto" // Let the image height adjust automatically
            image={product.imageUrl}
            title={`${product.brand} | ${product.title}`}
            alt={product.brand}
            onClick={() =>
              navigate(`/products/view/${categoryName}/${productId}`)
            }
            sx={{
              cursor: "pointer",
              borderRadius: "10px",
              transition: "transform 0.3s ease",
              "&:hover": {
                transform: "scale(0.86) translateY(-22px)", // Scale down and move up
              },
            }}
          />
        </RevealFade>
        <CardContent>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              top: "68%", // Adjust positioning as needed
              width: "100%",
              left: "0%",
              background: "#ffffff50",
              backdropFilter: "blur(50px)",
              color: "#000",
              pb: 2,
            }}
          >
            <RevealFade>
              <Typography
                gutterBottom
                component="div"
                sx={{
                  px: {xs:1,sm:1,md:2},
                  pt: 1,
                  fontFamily: "Poppins",
                  fontWeight: "550",
                  fontSize: { xs: "14px", sm: "16px", md: "18px",lg:"20px",xl:"20px" },
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                  textIndent: "auto",
                  height: "1em",
                  maxHeight: "1.5em",
                  lineHeight: "1em",
                  mb: 1,
                }}
              >
                {product.brand?.toUpperCase()}
              </Typography>
            </RevealFade>
            <RevealFade>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2, // Limit to 2 lines
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  textAlign: "left",
                  textIndent: "auto",
                  fontWeight: "500",
                  px: {xs:1,sm:1,md:2},
                  mb: 1,
                  height: { xs: "2em", sm: "2.4em", md: "3em" },
                  maxHeight: { xs: "2em", sm: "2.4em", md: "3em" }, // Adjust based on your line height
                  lineHeight: { xs: "1em", sm: "1.2em", md: "1.5em" }, // Adjust based on your preference
                }}
              >
                {product.title}
              </Typography>
            </RevealFade>
            <RevealFade>
              <Grid container spacing={1} sx={{}}>
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    // flexWrap: "wrap",
                    justifyContent: "space-between",
                    height: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Ubuntu",
                      fontWeight: "bold",
                      zIndex: "900",
                      pl: {xs:1,sm:1,md:2},
                      fontSize: { xs: "15px", sm: "17px", md: "18px",lg:"20px" },
                    }}
                  >
                    &#8377;{product.price?.toFixed(2)}
                  </Typography>
                  <Box sx={{ mr: {xs:1,sm:1,md:1,lg:2} }}>
                    <CustomTooltip
                      title="Add To Wishlist"
                      placement="top"
                      bgColor="#FF0040"
                    >
                      <IconButton
                        size="small"
                        sx={{
                          background: "transparent",
                          color: "grey", // Green color for add to cart
                          borderRadius: "10px",
                          p: 1,
                          width: { xs: "22px", sm: "24px", md: "26px", lg:"28px",xl:"30px" },
                          height: { xs: "22px", sm: "24px", md: "26px", lg:"28px",xl:"30px" },
                          mr: "3px",
                          // border: "2px solid #FF0040",
                          "&:hover": {
                            background: "transparent",
                            color: "#FF0040", // White text on hover
                          },
                        }}
                      >
                        <FavoriteBorderOutlined sx={{ fontSize: { xs: "22px", sm: "24px",md: "26px", lg:"28px",xl:"30px" } }} />
                      </IconButton>
                    </CustomTooltip>
                    <CustomTooltip
                      title="Add To Cart"
                      placement="top"
                      bgColor="#28a745"
                    >
                      <IconButton
                        onClick={() => clickAddToCart(product)}
                        size="small"
                        sx={{
                          background: "transparent",
                          color: "#28a745", // Green color for add to cart
                          borderRadius: "8px",
                          p: { xs: "10px", sm: "12px",md:"12px",lg:"14px" },
                          mx: "3px",
                          width: { xs: "18px", sm: "25px", md: "27px",lg:"29px" },
                          height: { xs: "17px", sm: "25px",md: "27px",lg:"29px" },
                          border: "2px solid #28a745", // Matching green border
                          "&:hover": {
                            background: "#28a745", // Green background on hover
                            color: "white", // White text on hover
                          },
                        }}
                      >
                        <AddShoppingCartRounded sx={{ fontSize: { xs: "17px", sm: "22px", md: "22px",lg:"26px" } }} />
                      </IconButton>
                    </CustomTooltip>
                  </Box>
                </Grid>
              </Grid>
            </RevealFade>
          </Box>
          {/* <Divider variant="middle" />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              px: 3,
              height: "100%",
              alignItems: "center",
              py: 2,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: "Ubuntu",
                fontWeight: "bold",
                zIndex: "900",
                pl: 2,
              }}
            >
              &#8377;{product.price?.toFixed(2)}
            </Typography>
            <div>
              <ul className="example-2">
                <li className="icon-content">
                  <a
                    data-social="Add to cart"
                    aria-label="Add to cart"
                    onClick={() => clickAddToCart(product)}
                  >
                    <div className="filled"></div>
                    <AddShoppingCartRounded />
                  </a>
                  <div className="tooltip">Add to Cart</div>
                </li>
              </ul>
            </div>
          </Box> */}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductCard2;
